import { useEffect, useRef } from 'react';

import { getDocument } from '@atlaskit/browser-apis';
import { fg } from '@atlaskit/platform-feature-flags';

interface UseUpdateEditorSizeProps {
	editorViewElement: HTMLElement;
	isInDocumentStreamingEnabled?: boolean;
	modalElement: HTMLElement | null;
}

export const HEIGHT_ELEMENT_CLASS_NAME = 'ai-height-div';
/**
 * whenever we update modal size - we want to update editor size to accommodate for it
 * This hook listens for modal size changes, and updates editor content area height accordingly
 */
export function useUpdateEditorHeight({
	modalElement,
	editorViewElement,
	isInDocumentStreamingEnabled,
}: UseUpdateEditorSizeProps): void {
	const heightDivRef = useRef<HTMLDivElement | null>(null);

	// listen to changes to modal size
	useEffect(() => {
		if (!modalElement) {
			return;
		}

		const resizeObserver = new ResizeObserver((entries) => {
			if (!heightDivRef.current || !modalElement) {
				return;
			}

			const modalEntry = entries.find((e) => e.target === modalElement);
			// entry should always exist for modaRef as we're observing on it, this check makes ts happy
			if (!modalEntry) {
				return;
			}
			const modalBottom =
				// Ignored via go/ees005
				// eslint-disable-next-line @atlaskit/editor/no-as-casting
				(modalEntry.target as HTMLElement).offsetTop + modalEntry.contentRect.height;
			const editorBottom = editorViewElement.offsetTop + editorViewElement.offsetHeight;

			// The height of the extra div should be expanded to reach
			// the bottom of the modal -- so that no modal content is outside
			// the editor
			const heightToSet = Math.max(modalBottom - editorBottom, 0);
			heightDivRef.current.style.height = `${heightToSet}px`;
		});

		resizeObserver.observe(modalElement);

		return () => {
			resizeObserver.disconnect();
		};
	}, [modalElement, editorViewElement]);

	// Create an element for adjusting height of editor when modal is visible
	useEffect(() => {
		if (heightDivRef.current) {
			// In future react will fire useEffect twice in dev mode
			// this early exit avoids issues with this
			return;
		}
		const doc = getDocument();
		if (!doc) {
			return;
		}
		const newDiv = doc.createElement('div');
		newDiv.className = HEIGHT_ELEMENT_CLASS_NAME;
		newDiv.style.height = `0px`;

		if (isInDocumentStreamingEnabled) {
			if (fg('platform_editor_ai_migrate_from_xstate')) {
				// Position directly after the editor element to avoid
				// selector ambiguity in nested editor scenarios.
				editorViewElement.after(newDiv);
			} else {
				// Ignored via go/ees005
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				const parent = editorViewElement.parentNode!;
				// Find the editor textarea element by ID
				const targetChild = parent.querySelector('#ak-editor-textarea') as HTMLElement | null;

				if (targetChild) {
					targetChild.after(newDiv);
				} else {
					// Ignored via go/ees005
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					editorViewElement.parentNode!.appendChild(newDiv);
				}
			}
		} else {
			// Ignored via go/ees005
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			editorViewElement.parentNode!.appendChild(newDiv);
		}
		heightDivRef.current = newDiv;

		return () => {
			if (fg('platform_editor_ai_migrate_from_xstate')) {
				newDiv.parentNode?.removeChild(newDiv);
			} else {
				editorViewElement.parentNode?.removeChild(newDiv);
			}
		};
	}, [editorViewElement, isInDocumentStreamingEnabled]);
}
